/**
 * Resolves investor API aliases and base URLs from the Amplify REST runtime configuration.
 */
import { Amplify } from "aws-amplify";
import { isRecord } from "./type-guards";

const DEFAULT_PLAID_API_ALIAS = "PlaidCashAPI";
const DEFAULT_IDENTITY_API_ALIAS = "IdentityAPI";
const API_ALIAS_RETRY_BACKOFF_MS = [100, 300, 700, 1500, 3000] as const;

type RestApiConfig = {
  endpoint?: string;
  region?: string;
};

type ApiAliasResolutionOptions = {
  apiLabel: string;
  preferredAlias: string;
  endpointHint: string;
  didAttemptReload: () => boolean;
  markReloadAttempted: () => void;
};

export type InvestorApiRuntimeContext = {
  availableApiAliases: string[];
  resolvedPlaidAlias: string | null;
  resolvedIdentityAlias: string | null;
};

export type ResolvedRestApiRuntime = {
  alias: string;
  endpoint: string;
};

let didAttemptPlaidConfigReload = false;
let didAttemptIdentityConfigReload = false;

export function resetAliasResolutionState(): void {
  if (process.env.NODE_ENV !== "test") {
    throw new Error("resetAliasResolutionState is only available in tests");
  }
  if (didAttemptPlaidConfigReload) {
    didAttemptPlaidConfigReload = false;
  }
  if (didAttemptIdentityConfigReload) {
    didAttemptIdentityConfigReload = false;
  }
}

function getConfiguredRestApis(): Record<string, RestApiConfig> {
  const rawConfig = Amplify.getConfig();
  if (!isRecord(rawConfig)) {
    return {};
  }

  const apiConfig = rawConfig.API;
  if (!isRecord(apiConfig)) {
    return {};
  }

  const restConfig = apiConfig.REST;
  if (!isRecord(restConfig)) {
    return {};
  }

  return restConfig as Record<string, RestApiConfig>;
}

function resolveApiAliasFromConfig(
  restApis: Record<string, RestApiConfig>,
  preferredAlias: string,
  endpointHint?: string,
): string | null {
  if (restApis[preferredAlias]) {
    return preferredAlias;
  }

  if (!endpointHint) {
    return null;
  }

  const normalizedEndpointHint = endpointHint.toLowerCase();
  for (const [apiAlias, apiConfig] of Object.entries(restApis)) {
    if (typeof apiConfig.endpoint !== "string") {
      continue;
    }
    if (apiConfig.endpoint.toLowerCase().includes(normalizedEndpointHint)) {
      return apiAlias;
    }
  }

  return null;
}

function buildApiConfigError(
  apiLabel: string,
  preferredAlias: string,
  endpointHint: string,
  availableApis: string[],
): Error {
  if (availableApis.length === 0) {
    return new Error(
      `${apiLabel} API configuration is missing at runtime. No Amplify REST APIs are configured.`,
    );
  }
  return new Error(
    `${apiLabel} API configuration is missing at runtime. Expected "${preferredAlias}" or a REST API endpoint containing "${endpointHint}". Available REST APIs: ${availableApis.join(", ")}`,
  );
}

function waitForAliasRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

async function resolveApiAliasWithReloadAndRetry(
  options: ApiAliasResolutionOptions,
): Promise<{
  resolvedAlias: string;
  restApis: Record<string, RestApiConfig>;
}> {
  let restApis = getConfiguredRestApis();
  let resolvedAlias = resolveApiAliasFromConfig(
    restApis,
    options.preferredAlias,
    options.endpointHint,
  );
  if (resolvedAlias) {
    return { resolvedAlias, restApis };
  }

  if (!options.didAttemptReload()) {
    options.markReloadAttempted();
    try {
      await import("@/aws-config");
    } catch (error) {
      console.warn(
        `Failed to reload aws-config while resolving ${options.apiLabel} API`,
        error,
      );
    }
    restApis = getConfiguredRestApis();
    resolvedAlias = resolveApiAliasFromConfig(
      restApis,
      options.preferredAlias,
      options.endpointHint,
    );
    if (resolvedAlias) {
      return { resolvedAlias, restApis };
    }
  }

  for (const delayMs of API_ALIAS_RETRY_BACKOFF_MS) {
    await waitForAliasRetry(delayMs);
    restApis = getConfiguredRestApis();
    resolvedAlias = resolveApiAliasFromConfig(
      restApis,
      options.preferredAlias,
      options.endpointHint,
    );
    if (resolvedAlias) {
      return { resolvedAlias, restApis };
    }
  }

  throw buildApiConfigError(
    options.apiLabel,
    options.preferredAlias,
    options.endpointHint,
    Object.keys(restApis),
  );
}

function getEndpointForResolvedAlias(
  restApis: Record<string, RestApiConfig>,
  alias: string,
  apiLabel: string,
): string {
  const endpoint = restApis[alias]?.endpoint;
  if (typeof endpoint === "string" && endpoint.trim() !== "") {
    return endpoint;
  }
  throw new Error(
    `${apiLabel} API endpoint is missing for resolved alias "${alias}".`,
  );
}

export async function getPlaidApiRuntime(): Promise<ResolvedRestApiRuntime> {
  const { resolvedAlias: alias, restApis } =
    await resolveApiAliasWithReloadAndRetry({
      apiLabel: "Plaid",
      preferredAlias: DEFAULT_PLAID_API_ALIAS,
      endpointHint: "plaidcash",
      didAttemptReload: () => didAttemptPlaidConfigReload,
      markReloadAttempted: () => {
        didAttemptPlaidConfigReload = true;
      },
    });

  return {
    alias,
    endpoint: getEndpointForResolvedAlias(restApis, alias, "Plaid"),
  };
}

export async function getIdentityApiRuntime(): Promise<ResolvedRestApiRuntime> {
  const { resolvedAlias: alias, restApis } =
    await resolveApiAliasWithReloadAndRetry({
      apiLabel: "Identity",
      preferredAlias: DEFAULT_IDENTITY_API_ALIAS,
      endpointHint: "identity",
      didAttemptReload: () => didAttemptIdentityConfigReload,
      markReloadAttempted: () => {
        didAttemptIdentityConfigReload = true;
      },
    });

  return {
    alias,
    endpoint: getEndpointForResolvedAlias(restApis, alias, "Identity"),
  };
}

export async function getPlaidApiAlias(): Promise<string> {
  const { alias } = await getPlaidApiRuntime();
  return alias;
}

export async function getIdentityApiAlias(): Promise<string> {
  const { alias } = await getIdentityApiRuntime();
  return alias;
}

export function getInvestorApiRuntimeContext(): InvestorApiRuntimeContext {
  const restApis = getConfiguredRestApis();
  return {
    availableApiAliases: Object.keys(restApis),
    resolvedPlaidAlias: resolveApiAliasFromConfig(
      restApis,
      DEFAULT_PLAID_API_ALIAS,
      "plaidcash",
    ),
    resolvedIdentityAlias: resolveApiAliasFromConfig(
      restApis,
      DEFAULT_IDENTITY_API_ALIAS,
      "identity",
    ),
  };
}

export async function getIdentityApiBaseUrl(): Promise<string> {
  const { endpoint } = await getIdentityApiRuntime();
  return endpoint;
}

export async function getPlaidApiBaseUrl(): Promise<string> {
  const { endpoint } = await getPlaidApiRuntime();
  return endpoint;
}
